import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Header,
  Segment,
  Button,
  Menu,
  Statistic,
  Icon,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import { getDashboardSummary } from '../api/DashboardApi';
import { DashboardSummaryDTO } from '../dto/DashboardDTOs';

const HomeForAdmin: React.FC = () => {
  // TODO: 실제 인증 토큰을 localStorage나 context에서 가져오도록 수정
  const authToken = localStorage.getItem('accessToken') || '';

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getDaysAgo(7);
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardSummaryDTO | null>(null);
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'dateInput') {
      setDateInputEnabled(true);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const result = await getDashboardSummary(authToken, startDate, endDate);
      if (result && result.errorCode === 0) {
        setDashboardData(result.response);
      }
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [startDate, endDate]);

  const handleSearch = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <Container fluid>
        <Dimmer active inverted>
          <Loader size="large">데이터를 불러오는 중...</Loader>
        </Dimmer>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container fluid>
        <Header as="h2">데이터를 불러올 수 없습니다.</Header>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <Header as="h2" style={{ marginBottom: 16 }}>
        1. 대시보드
      </Header>

      {/* 기간 선택 + 상단 탭 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ marginRight: 8, fontWeight: 500 }}>시작일</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <span style={{ margin: '0 4px' }}>~</span>
          <span style={{ marginRight: 8, fontWeight: 500 }}>종료일</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <Button primary style={{ marginLeft: 12 }} onClick={handleSearch}>
            검색
          </Button>
        </div>

        <Menu compact>
          <Menu.Item
            active={selectedPeriod === 'today'}
            onClick={() => handlePeriodChange('today')}
          >
            오늘
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === '7days'}
            onClick={() => handlePeriodChange('7days')}
          >
            7일
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === '30days'}
            onClick={() => handlePeriodChange('30days')}
          >
            30일
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === 'dateInput'}
            onClick={() => handlePeriodChange('dateInput')}
          >
            날짜입력
          </Menu.Item>
        </Menu>
      </Segment>

      {/* 카드 영역 */}
      <Grid columns={3} stackable style={{ marginTop: 16 }}>
        {/* 첫 번째 줄 */}
        <Grid.Row stretched>
          {/* 회원 현황 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                회원 현황: 전체사용자수 / 신규 가입자 수
              </Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="users" color="blue" /> {dashboardData.totalUsers.toLocaleString()}
                  </Statistic.Value>
                  <Statistic.Label>전체 사용자</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="user plus" color="blue" /> {dashboardData.newUsers}
                  </Statistic.Value>
                  <Statistic.Label>신규 가입자</Statistic.Label>
                </Statistic>
              </div>
            </Segment>
          </Grid.Column>

          {/* 신고·검토 대기 요약 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                신고·검토 대기 요약
              </Header>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <Icon name="file alternate outline" color="blue" /> 신고요약
                  </span>
                  <strong>{dashboardData.reportSummary}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <Icon name="exclamation circle" color="blue" /> 신고·검토 대기
                  </span>
                  <strong>{dashboardData.reportReviewPending}</strong>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          {/* 진행중 이벤트 배너 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                진행중 이벤트(배너)
              </Header>
              <div
                style={{
                  height: 140,
                  borderRadius: 8,
                  background:
                    'linear-gradient(135deg, #1b4ed0 0%, #4f7df2 60%, #7fa1ff 100%)',
                  color: '#fff',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4 }}>
                  {dashboardData.ongoingEventBanner?.title || '진행 중 이벤트'}
                </div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>
                  {dashboardData.ongoingEventBanner?.description || ''}
                </div>
              </div>
            </Segment>
          </Grid.Column>
        </Grid.Row>

        {/* 두 번째 줄 */}
        <Grid.Row columns={3} stretched>
          {/* 현재 읽는 책 / 필사 진행 책 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                현재 읽는 책 권 수 / 필사 진행 책 권 수
              </Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="book" color="blue" /> {dashboardData.currentReadingBooks}
                  </Statistic.Value>
                  <Statistic.Label>읽는 중</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="edit outline" color="blue" /> {dashboardData.currentScriptingBooks}
                  </Statistic.Value>
                  <Statistic.Label>필사 진행</Statistic.Label>
                </Statistic>
              </div>
            </Segment>
          </Grid.Column>

          {/* 진행중 모임수 / 신규 모임 수 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                진행중 모임수 / 신규 모임 수
              </Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="users" color="blue" /> {dashboardData.ongoingGatherings}
                  </Statistic.Value>
                  <Statistic.Label>진행중</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="add user" color="blue" /> +{dashboardData.newGatherings}
                  </Statistic.Value>
                  <Statistic.Label>신규 모임</Statistic.Label>
                </Statistic>
              </div>
            </Segment>
          </Grid.Column>

          {/* 총 독서 시간 / 평균 세션 시간 */}
          <Grid.Column>
            <Segment raised style={{ height: '100%' }}>
              <Header as="h4" style={{ marginBottom: 16 }}>
                총 독서 시간 / 평균 세션 시간
              </Header>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="clock outline" color="blue" /> {dashboardData.totalReadingTime}
                  </Statistic.Value>
                  <Statistic.Label>총 독서 시간</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>
                    <Icon name="hourglass half" color="blue" /> {dashboardData.averageSessionTime}
                  </Statistic.Value>
                  <Statistic.Label>평균 세션</Statistic.Label>
                </Statistic>
              </div>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
};

export default HomeForAdmin;
